import React, { lazy, Suspense, useState, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QuizProvider } from './contexts/QuizContext';
import { AuthProvider } from './contexts/AuthContext';
import { IntroVideoProvider } from './contexts/IntroVideoContext';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { Login } from './components/Auth/Login';
import { AuthCallback } from './components/Auth/AuthCallback';
import { QuizBackupsPage } from './pages/QuizBackupsPage';
import { ToastProvider } from './components/Toast';
import { NewVersionBanner } from './components/NewVersionBanner';

// Create a context for managing dialog state
interface DialogContextType {
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
}

export const DialogContext = createContext<DialogContextType>({
  isDialogOpen: false,
  setIsDialogOpen: () => {},
});

// Create a provider component
const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  return (
    <DialogContext.Provider value={{ isDialogOpen, setIsDialogOpen }}>
      {children}
    </DialogContext.Provider>
  );
};

// Each release renames the page files and removes the old ones, so a tab opened
// before a release fails when it opens a page it hasn't loaded yet, and the
// screen goes blank. Reload once to pick up the new release; the timestamp
// stops a reload loop if the file is missing for some other reason.
const PAGE_RELOAD_KEY = 'page-load-reload-at';
const lazyWithReload = <T extends React.ComponentType<any>>(load: () => Promise<{ default: T }>) =>
  lazy(() =>
    load().catch(error => {
      let reloadedRecently = true;
      try {
        reloadedRecently = Date.now() - Number(sessionStorage.getItem(PAGE_RELOAD_KEY) ?? 0) < 30000;
        if (!reloadedRecently) sessionStorage.setItem(PAGE_RELOAD_KEY, String(Date.now()));
      } catch {
        // No sessionStorage: don't risk a loop.
      }
      if (reloadedRecently) throw error;
      window.location.reload();
      return new Promise<{ default: T }>(() => {});
    })
  );

const AdminPage = lazyWithReload(() => import('./pages/AdminPage').then(module => ({ default: module.AdminPage })));
const QuizSelectionPage = lazyWithReload(() => import('./pages/QuizSelectionPage').then(module => ({ default: module.QuizSelectionPage })));
const ManageQuizzesPage = lazyWithReload(() => import('./pages/ManageQuizzesPage').then(module => ({ default: module.ManageQuizzesPage })));
const AdminQuizPreview = lazyWithReload(() => import('./pages/AdminQuizPreview').then(module => ({ default: module.AdminQuizPreview })));
const TakeQuizPage = lazyWithReload(() => import('./pages/TakeQuizPage').then(module => ({ default: module.TakeQuizPage })));

const AnalyticsPage = lazyWithReload(() => import('./pages/AnalyticsPageNew').then(module => ({ default: module.AnalyticsPageNew })));
const MyAnalyticsPage = lazyWithReload(() => import('./pages/MyAnalyticsPage').then(module => ({ default: module.MyAnalyticsPage })));
const FAQPage = lazyWithReload(() => import('./pages/FAQPage').then(module => ({ default: module.FAQPage })));
const VerifyCertificatePage = lazyWithReload(() => import('./pages/VerifyCertificatePage').then(module => ({ default: module.VerifyCertificatePage })));

const MainContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div>
      {children}
    </div>
  );
};

const App: React.FC = () => {
  // Only clear storage if there are actual corruption indicators
  React.useEffect(() => {
    const clearCorruptedStorageOnAppStart = () => {
      try {
        // More conservative corruption check - only clear if we have invalid tokens or error states
        const authTokenExists = !!localStorage.getItem('sb-tqmapdzcaqbrkhlrcgxb-auth-token');
        const hasErrorState = localStorage.getItem('supabase.auth.error') !== null;
        const hasInvalidSession = localStorage.getItem('supabase.auth.invalid') !== null;
        
        // Only clear if we detect actual problems, not missing optional keys
        if (hasErrorState || hasInvalidSession) {
          console.log('🧹 App: Authentication error detected, clearing corrupted storage...');
          
          // Clear only problematic items, preserve valid sessions
          localStorage.removeItem('supabase.auth.error');
          localStorage.removeItem('supabase.auth.invalid');
          
          console.log('✅ App: Error state cleanup completed');
        } else if (authTokenExists) {
          console.log('ℹ️ App: Valid session found, preserving authentication state');
        }
      } catch (error) {
        console.warn('⚠️ App: Error during storage check:', error);
      }
    };
    
    clearCorruptedStorageOnAppStart();
  }, []);

  return (
    <AuthProvider>
      <IntroVideoProvider>
        <QuizProvider>
          <DialogProvider>
            <ToastProvider>
              <Router>
                <div className="flex flex-col min-h-screen bg-surface relative">
                  <main className="flex-grow w-full">
                    <Suspense fallback={<div className="text-slate-600 flex items-center justify-center min-h-screen">Loading...</div>}>
                      <MainContent>
                        <Routes>
                          <Route path="/login" element={<Login />} />
                          <Route path="/auth/callback" element={<AuthCallback />} />
                          {/* Public: the destination of the QR on every certificate
                              PDF. External verifiers have no login. */}
                          <Route path="/verify/:certificateId" element={<VerifyCertificatePage />} />
                          <Route
                            path="/"
                            element={
                              <ProtectedRoute requireAuth={true}>
                                <QuizSelectionPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/quiz/:quizId"
                            element={
                              <ProtectedRoute requireAuth={true}>
                                <TakeQuizPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin"
                            element={
                              <ProtectedRoute requireAuth={true} requireAdmin={true}>
                                <AdminPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/manage-quizzes"
                            element={
                              <ProtectedRoute requireAuth={true} requireAdmin={true}>
                                <ManageQuizzesPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/preview/:quizId"
                            element={
                              <ProtectedRoute requireAuth={true} requireAdmin={true}>
                                <AdminQuizPreview />
                              </ProtectedRoute>
                            }
                          />

                          <Route
                            path="/backups"
                            element={
                              <ProtectedRoute requireAuth={true} requireAdmin={true}>
                                <QuizBackupsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/analytics"
                            element={
                              <ProtectedRoute requireAuth={true} requireAdmin={true}>
                                <AnalyticsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/my-analytics"
                            element={
                              <ProtectedRoute requireAuth={true}>
                                <MyAnalyticsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/faq"
                            element={
                              <ProtectedRoute requireAuth={true}>
                                <FAQPage />
                              </ProtectedRoute>
                            }
                          />
                          {/* Unknown paths go to the dashboard, NOT /login:
                              the Login page clears the stored session on
                              mount as corruption cleanup, so sending a
                              logged-in user there logs them out. ProtectedRoute
                              still forwards logged-out visitors to /login. */}
                          <Route path="*" element={<Navigate to="/" />} />

                        </Routes>
                      </MainContent>
                    </Suspense>
                  </main>
                  <NewVersionBanner />
                </div>
              </Router>
            </ToastProvider>
          </DialogProvider>
        </QuizProvider>
      </IntroVideoProvider>
    </AuthProvider>
  );
};

export default App;
